import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const STORAGE_KEY = 'active_workout';

interface StoredWorkout {
  id: string;
  startedAt: string;
  exercises: any[];
  notes?: string;
  hyroxName?: string;
}

function readName(w: StoredWorkout): string {
  if (w.hyroxName) return w.hyroxName;
  if (w.notes) {
    try {
      const p = JSON.parse(w.notes);
      if (p?.customName && typeof p.customName === 'string') return p.customName;
    } catch { /* noop */ }
  }
  return 'Workout in progress';
}

const MIN_KEY = 'active_workout_minimised';

export function ActiveWorkoutBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [workout, setWorkout] = useState<StoredWorkout | null>(null);
  const [minimised, setMinimised] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const check = () => {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try { setWorkout(JSON.parse(saved)); } catch { setWorkout(null); }
      } else {
        setWorkout(null);
      }
      setMinimised(!!localStorage.getItem(MIN_KEY));
    };
    check();
    window.addEventListener('storage', check);
    window.addEventListener('active-workout-changed', check);
    window.addEventListener('focus', check);
    const interval = setInterval(check, 5000);
    return () => {
      window.removeEventListener('storage', check);
      window.removeEventListener('active-workout-changed', check);
      window.removeEventListener('focus', check);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!workout) return;
    const tick = () => {
      const start = new Date(workout.startedAt).getTime();
      setElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [workout]);

  if (location.pathname === '/workout/active') return null;
  if (!workout) return null;
  if (minimised) return null;

  const formatTime = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  const name = readName(workout);
  const completedSets = workout.exercises?.reduce(
    (sum: number, ex: any) => sum + (ex.sets?.filter((s: any) => s.completed).length || 0),
    0,
  ) || 0;
  const exerciseCount = workout.exercises?.length || 0;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-50 shadow-md pt-safe-status"
      style={{ backgroundColor: '#0091FF' }}
    >
      <button
        onClick={() => navigate('/workout/active')}
        className={cn(
          'w-full flex items-center justify-between gap-3 px-4 pt-2 pb-2.5',
          'active:opacity-90 transition-opacity',
        )}
        style={{ color: '#FFFFFF' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          {/* Live dot */}
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-60 animate-ping" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
          </span>
          <div className="text-left min-w-0">
            <p className="text-subhead font-semibold leading-tight truncate">{name}</p>
            <p className="text-caption leading-tight opacity-80 truncate">
              {exerciseCount} exercise{exerciseCount !== 1 ? 's' : ''} · {completedSets} set{completedSets !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span
            className="text-subhead font-semibold tabular-nums px-2.5 py-1 rounded-full"
            style={{ backgroundColor: 'rgba(255,255,255,0.18)' }}
          >
            {formatTime(elapsed)}
          </span>
          <ChevronRight className="h-4 w-4 opacity-90" />
        </div>
      </button>
    </div>
  );
}
